/**
 * BIG NUMBERS, GROUPED THE WAY THIS PRODUCT WRITES THEM.
 *
 * ═══ 🚨 THE SEPARATOR IS AN APOSTROPHE AND IT IS NOT `Intl`'s CHOICE ═══
 *
 * `272'046`, never `272,046` and never `272.046`.
 *
 * This was `Intl.NumberFormat(language)`, which sounds like the careful answer and is the wrong
 * one here. It groups by the reader's LANGUAGE, so the same tonnage renders `272,046` in English
 * and `272.046` in German — and that second one reads as two hundred and seventy-two point
 * something to anyone who has just switched languages, because a comma and a full stop swap
 * meanings between the two. A training total that changes shape when the app's language changes
 * is a number an athlete has to re-learn.
 *
 * The apostrophe is unambiguous in both: it is not a decimal point in any locale this app ships,
 * so `272'046` is six digits to everybody. That makes it a product decision rather than a
 * locale one, which is why it is a constant here instead of a formatter option.
 *
 * ⚠️ IT IS FOR MAGNITUDES, NOT FOR EVERY NUMBER. A rep count, a set count, a round — anything
 * under a thousand — comes out unchanged, so this is safe to use everywhere and only visible
 * where it is needed. It is NOT for a year, an id or a version, which are digits that happen to
 * be long rather than quantities; those must never be grouped at all.
 */

/** What goes between each group of three. See the header: a product decision, not a locale one. */
const GROUP_SEPARATOR = "'";

/**
 * A whole number with its thousands grouped.
 *
 * Rounds first, because every caller is drawing a magnitude — a volume, a rep total, a tonnage
 * — and a fractional kilogram in a six-digit total is noise a reader takes for precision.
 *
 * ⚠️ THE SIGN IS HANDLED SEPARATELY so a negative never comes out as `-'123`. Nothing in this
 * app currently shows a negative quantity; costing one line to not be wrong if something ever
 * does is the better trade than finding out later.
 */
export function grouped(value: number): string {
  if (!Number.isFinite(value)) return "0";

  const rounded = Math.round(value);
  const sign = rounded < 0 ? "-" : "";
  const digits = Math.abs(rounded).toString();

  /* Right to left in threes. `replace` with a lookahead does this in one line, and the
     lookahead is what stops it inserting a separator before the very first digit. */
  return sign + digits.replace(/\B(?=(\d{3})+(?!\d))/g, GROUP_SEPARATOR);
}

/**
 * A weight, with at most one decimal, grouped.
 *
 * ⚠️ ONE DECIMAL AND ONLY WHERE IT SAYS SOMETHING. `137.5 kg` is a real load and `137.0 kg` is
 * how nobody writes one, so a whole number stays whole. That is also what keeps a column of
 * weights aligned in the common case, where every one of them is a round 2.5 kg step.
 */
export function groupedWeight(value: number): string {
  if (!Number.isFinite(value)) return "0";

  const rounded = Math.round(value * 10) / 10;
  const whole = Math.trunc(rounded);
  const decimal = Math.abs(Math.round((rounded - whole) * 10));

  return decimal === 0 ? grouped(rounded) : `${grouped(whole)}.${decimal}`;
}
